import titleCase from 'title-case';

/**
 * Formats a name to title case while preserving uppercase initials/abbreviations.
 *
 * Examples:
 * - "cj" -> "CJ" (preserves 2-letter initials)
 * - "john" -> "John" (regular title case)
 * - "mary ann" -> "Mary Ann" (regular title case)
 * - "cj zach" -> "CJ Zach" (preserves initials, formats regular names)
 * - "MC donald" -> "MC Donald" (preserves 2-letter uppercase)
 *
 * @param {string} name - The name to format
 * @returns {string} The formatted name
 */
export const formatName = (name) => {
  if (!name) return '';

  // First apply title case to the entire name
  const titleCased = titleCase(name);

  // Split by spaces to process each word
  const words = titleCased.split(' ');

  // Process each word
  const formattedWords = words.map(word => {
    // Check if the word is 1-2 characters and could be an initial/abbreviation
    // by checking if the original (before title case) was all uppercase
    const originalWord = name.split(' ').find(w =>
      w.toLowerCase() === word.toLowerCase()
    );

    if (originalWord && originalWord.length <= 2 && originalWord === originalWord.toUpperCase()) {
      // This was originally uppercase and is 1-2 chars, likely an initial
      return word.toUpperCase();
    }

    return word;
  });

  return formattedWords.join(' ');
};

/**
 * Formats a full name (last, first middle) while preserving initials.
 *
 * @param {string} lastName - Last name
 * @param {string} firstName - First name
 * @param {string} middleName - Middle name (optional)
 * @returns {string} Formatted full name
 */
export const formatFullName = (lastName, firstName, middleName = '') => {
  const formattedLastName = formatName(lastName);
  const formattedFirstName = formatName(firstName);
  const formattedMiddleName = middleName ? formatName(middleName) : '';

  if (formattedMiddleName) {
    return `${formattedLastName}, ${formattedFirstName} ${formattedMiddleName}`;
  }

  return `${formattedLastName}, ${formattedFirstName}`;
};

/**
 * Normalizes a name for fuzzy comparison. Strips diacritics (so "ñ" matches "n"
 * and accented vowels match their plain forms), lowercases, and drops separators
 * (spaces, hyphens, dots, apostrophes) so double/trailing spaces and punctuation
 * differences don't block a match.
 *
 * @param {string} text
 * @returns {string}
 */
export const normalizeForMatch = (text) =>
  (text || '')
    .normalize('NFD')                 // decompose accents: ñ -> n + combining tilde
    .replace(/[̀-ͯ]/g, '')  // strip combining diacritical marks
    .toLowerCase()
    .replace(/[\s\-.']/g, '');        // drop spaces, hyphens, dots, apostrophes

/**
 * Levenshtein edit distance (two-row iterative). O(n*m); trivial for short names.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i++) {
    const curr = [i + 1];
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      curr[j + 1] = Math.min(prev[j + 1] + 1, curr[j] + 1, prev[j] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
};

/**
 * Similarity of two already-normalized name parts, in [0, 1].
 * - exact match -> 1
 * - one contains the other (nicknames, missing middle name) -> 0.95
 * - otherwise 1 - editDistance/maxLength
 * Returns 0 when either side is empty so a missing field can't inflate a match.
 *
 * @param {string} x - normalized
 * @param {string} y - normalized
 * @returns {number}
 */
const fieldScore = (x, y) => {
  if (!x || !y) return 0;
  if (x === y) return 1;
  if (x.includes(y) || y.includes(x)) return 0.95;
  return 1 - levenshtein(x, y) / Math.max(x.length, y.length);
};

/**
 * Combined first + last name similarity in [0, 1]. Equal weighting on first and
 * last. Also checks the swapped pairing (first<->last) so a "Last, First"
 * data-entry reversal still scores well; returns the better of the two.
 *
 * @param {string} aFirst
 * @param {string} aLast
 * @param {string} bFirst
 * @param {string} bLast
 * @returns {number}
 */
export const scoreNameMatch = (aFirst, aLast, bFirst, bLast) => {
  const fa = normalizeForMatch(aFirst);
  const fb = normalizeForMatch(bFirst);
  const la = normalizeForMatch(aLast);
  const lb = normalizeForMatch(bLast);

  const direct = 0.5 * fieldScore(fa, fb) + 0.5 * fieldScore(la, lb);
  const swapped = 0.5 * fieldScore(fa, lb) + 0.5 * fieldScore(la, fb);
  return Math.max(direct, swapped);
};
